import React, { useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Spinner } from "./components/ui/spinner";
import { Skeleton, SkeletonCard } from "./components/ui/skeleton";
import { TooltipProvider } from "./components/ui/tooltip";
import LoginButton from "./components/LoginButton";
import UserOnboarding from "./components/UserOnboarding";
import WelcomeNoRole from "./components/WelcomeNoRole";
import RoleBasedNavigation from "./components/Navigation/RoleBasedNavigation";
import RoleBasedRoutes from "./components/Navigation/RoleBasedRoutes";
import AppLayout from "./components/Layout/AppLayout";
import { UserProvider, useUser } from "./contexts/UserContext";
import PendingInvitesCheck from "./components/Invites/PendingInvitesCheck";
import "./App.css";

// Component to handle the main app content
const PublicRouteHandler = () => {
  // No more public invite routes needed - everything is handled post-authentication
  return <AppContent />;
};

// Create a separate component for the main app content
const AppContent = () => {
  const { userProfile, isLoading, hasAnyRole, refreshProfile } = useUser();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [inviteCheckComplete, setInviteCheckComplete] = useState(false);

  // Step 1: Show loading skeleton while Auth0 and user profile are loading
  if (isLoading) {
    return (
      <TooltipProvider>
        <div className="flex justify-center items-center h-screen">
          <div className="text-center space-y-6 w-full max-w-md">
            <SkeletonCard className="p-8">
              <div className="space-y-4">
                <Skeleton className="h-12 w-12 mx-auto rounded-full" />
                <Skeleton className="h-4 w-48 mx-auto" />
                <Skeleton className="h-3 w-32 mx-auto" />
              </div>
            </SkeletonCard>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Step 2: Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <TooltipProvider>
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center justify-center h-screen gap-8">
            <h1 className="text-6xl font-bold">Welcome to Zentra</h1>
            <p className="text-xl text-muted-foreground">
              Please log in to continue
            </p>
            <LoginButton />
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Step 3: *** THE VERY FIRST THING AFTER AUTH0 AUTHENTICATION ***
  // Check for pending invites BEFORE making any other UI decisions
  if (isAuthenticated && !inviteCheckComplete) {
    return (
      <TooltipProvider>
        <div className="flex justify-center items-center h-screen">
          <div className="text-center space-y-6 w-full max-w-md">
            <SkeletonCard className="p-8">
              <div className="space-y-4">
                <Skeleton className="h-12 w-12 mx-auto rounded-full" />
                <Skeleton className="h-4 w-48 mx-auto" />
                <Skeleton className="h-3 w-32 mx-auto" />
              </div>
            </SkeletonCard>
            <p className="text-muted-foreground">
              Checking for pending invitations...
            </p>
          </div>
        </div>
        {/* This component handles the actual invite checking logic */}
        <PendingInvitesCheck
          userEmail={user?.email}
          onInvitesAccepted={async () => {
            try {
              // Refresh user profile to get new roles
              await refreshProfile();
            } catch (error) {
              console.error("Error refreshing profile:", error);
            }

            // Mark invite check as complete to trigger normal app flow
            // The app will then navigate the user based on their updated roles
            setInviteCheckComplete(true);
          }}
          onInviteCheckComplete={() => {
            setInviteCheckComplete(true);
          }}
        />
      </TooltipProvider>
    );
  }

  // Step 4: ONLY AFTER invite check is complete, proceed with normal app flow

  if (userProfile?.needsRegistration) {
    return (
      <TooltipProvider>
        <div className="container mx-auto px-6">
          <UserOnboarding
            user={user}
            onUserCreated={async () => {
              try {
                // Refresh user profile to get new roles
                await refreshProfile();
              } catch (error) {
                console.error("Error refreshing profile:", error);
              }
              // Let the normal app flow handle routing based on updated roles
            }}
            getAccessTokenSilently={getAccessTokenSilently}
          />
        </div>
      </TooltipProvider>
    );
  }

  if (!hasAnyRole) {
    return (
      <TooltipProvider>
        <div className="container mx-auto px-6">
          <WelcomeNoRole userProfile={userProfile} />
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <AppLayout>
        <RoleBasedRoutes />
      </AppLayout>
    </TooltipProvider>
  );
};

// Update main App component
function App() {
  return (
    <Router>
      <UserProvider>
        <PublicRouteHandler />
      </UserProvider>
    </Router>
  );
}

export default App;
